/**
 * Báo giá thiết bị — kiểu dữ liệu, giá trị mặc định và phép tính (thuần, không I/O).
 * BẢN SAO Y HỆT ở admin-hp-media-staging/src/pages/equipment-quote/calc.ts: sửa một bên thì chép sang bên kia
 * (server tính lại khi lưu / xuất Excel; trang quản trị tính để xem trước ngay).
 * Đặc tả: docs/bao-gia-thiet-bi/01-dac-ta-2026-10-27.md §3.
 */

export type QuoteStatus = 'DRAFT' | 'SENT' | 'SIGNED' | 'CANCELLED';
export const QUOTE_STATUSES: QuoteStatus[] = ['DRAFT', 'SENT', 'SIGNED', 'CANCELLED'];

/** CASH: Creator tự chuyển khoản · DEDUCT_INCOME: trừ dần vào thưởng/thu nhập Công ty chi · MIXED: cả hai. */
/** Tình trạng thiết bị lúc giao: NEW = mới 100%, USED = đã qua sử dụng. */
export type ItemCondition = 'NEW' | 'USED';
export const CONDITION_LABEL: Record<ItemCondition, string> = {
  NEW: 'Mới 100%',
  USED: 'Đã qua sử dụng',
};

export type PaymentMethod = 'CASH' | 'DEDUCT_INCOME' | 'MIXED';

export interface QuoteLine {
  /** Khoá ổn định trong một báo giá (để sửa dòng trên giao diện). */
  key: string;
  itemId?: number | null;
  category: string;
  name: string;
  /** Hãng sản xuất (chép từ danh mục lúc thêm dòng; KTV sửa được). */
  brand?: string;
  unit?: string;
  price: number;
  qty: number;
  /** Công ty hỗ trợ bao nhiêu % dòng này (0 = Creator tự trả toàn bộ, 100 = Công ty trả hết). */
  supportPercent: number;
  /** Số serial / IMEI — điền lúc bàn giao (Phụ lục 02). */
  serial?: string;
  note?: string;
  /** Ảnh sản phẩm: key trên Spaces (hp/<uuid>), chép từ danh mục lúc thêm dòng. */
  imageKey?: string;
  /** Mới hay cũ lúc giao cho Creator (mặc định NEW). */
  condition?: ItemCondition;
  /** Món tặng kèm mọi gói (danh mục isIncluded): Creator HP Media được miễn phí 100%. */
  included?: boolean;
  /** Có sẵn trong mọi gói nhưng Công ty ỨNG TRƯỚC: Creator trả lại 100% (trả dần theo Điều 3). */
  advance?: boolean;
}

export interface PaymentTerms {
  /** % mặc định áp cho dòng được hỗ trợ (giao diện dùng khi bấm "Hỗ trợ"). */
  supportPercent: number;
  /** Creator trả trước khi nhận thiết bị. */
  upfrontAmount: number;
  /** Phần còn lại trả trong bao nhiêu tháng (0 = trả hết một lần). */
  installmentMonths: number;
  paymentMethod: PaymentMethod;
  /** Chi phí đi lại / lắp đặt Công ty chi (phải hoàn nếu vi phạm). */
  travelSupportAmount: number;
}

export interface ContractTerms {
  /** Thời gian cam kết hoạt động; hết hạn mà đủ điều kiện → chuyển quyền sở hữu phần hỗ trợ cho Creator. */
  commitMonths: number;
  /** Không hoạt động liên tục bao nhiêu tháng thì phải hoàn trả toàn bộ. */
  inactivityMonths: number;
  /** Định nghĩa "có hoạt động" trong một tháng. */
  minLiveDaysPerMonth: number;
  minLiveHoursPerMonth: number;
  /** Số ngày phải trả lại thiết bị / tiền kể từ ngày nhận thông báo. */
  returnDays: number;
  /** Không liên lạc được bao nhiêu ngày liên tục thì coi là vi phạm. */
  unreachableDays: number;
  /** Phạt vi phạm: % trên giá trị phần nghĩa vụ bị vi phạm (BLDS 2015 Điều 418). */
  penaltyPercent: number;
  /** Lãi chậm trả %/năm (BLDS Điều 357, 468: tối đa 20%/năm; không thoả thuận = 10%/năm). */
  lateInterestPercentYear: number;
  /** Hết thời hạn cam kết + không vi phạm → thiết bị thuộc về Creator. */
  ownershipTransfer: boolean;
  /** Thoả thuận chọn Toà án nơi Công ty có trụ sở (BLTTDS 2015 Điều 39 khoản 1 điểm b). */
  disputeAtCompanyCourt: boolean;
  /** Creator đồng ý cho Công ty trừ khoản phải trả/hoàn vào thưởng, thu nhập Công ty chi trả. */
  allowDeductIncome: boolean;
  /**
   * Bên B trích bao nhiêu % thu nhập LIVE của tài khoản cá nhân để trả dần phần phải thanh toán / phải hoàn.
   * 0 = để trống (in dòng chấm, hai bên tự thoả thuận). Dừng khi đã trả đủ — không phải phí Đại lý.
   */
  incomeSharePercent: number;
  /** Trong thời gian dùng, Bên B được đề nghị mua đứt thiết bị theo 100% giá hoá đơn, trả một hay nhiều đợt. */
  allowBuyout: boolean;
  /** Thời hạn gắn tài khoản với Đại lý trên TikTok (năm) — chỉ để dẫn chiếu; 0 = không nhắc. */
  agencyTermYears: number;
  /** Rời Đại lý của Bên A trên TikTok khi còn mượn thiết bị = vi phạm (phải trả thiết bị). */
  leaveAgencyIsBreach: boolean;
  /**
   * Mức đền khi MẤT hoặc HỎNG KHÔNG BẢO HÀNH (theo giá hoá đơn): thiết bị nhận mới / nhận cũ.
   * Thay bảng khấu hao đều theo tháng (27/10: sau 9 tháng chỉ còn 25% → Công ty lỗ). Trả lại bình thường: không trừ gì.
   */
  compensateNewPercent: number;
  compensateUsedPercent: number;
}

export interface PersonInfo {
  fullName?: string;
  dob?: string;
  idNumber?: string;
  idIssuedDate?: string;
  idIssuedPlace?: string;
  address?: string;
  phone?: string;
  email?: string;
  tiktokId?: string;
  tiktokUid?: string;
  /** Chỉ người giám hộ: quan hệ với Creator. */
  relation?: string;
}

export interface QuoteData {
  lines: QuoteLine[];
  /** Creator của HP Media (mặc định có): món tặng kèm được miễn phí. Tắt = tính tiền như món thường. */
  hpCreator: boolean;
  payment: PaymentTerms;
  contract: ContractTerms;
  creator: PersonInfo;
  /** Creator dưới 18 tuổi: cha/mẹ/người giám hộ ký cùng. */
  guardian?: PersonInfo | null;
  contractNo?: string;
  signDate?: string;
  signPlace?: string;
  deliveryAddress?: string;
  /** Ghi chú in cuối báo giá. */
  notes?: string;
  /** Điều khoản thêm do KTV/Admin gõ tay (mỗi dòng một ý). */
  extraClauses?: string;
  /** Bàn giao & nghiệm thu (KTV) — xem HandoverInfo. */
  handover?: HandoverInfo | null;
}

export interface CompanyInfo {
  name: string;
  shortName: string;
  address: string;
  taxCode: string;
  representative: string;
  position: string;
  phone: string;
  email: string;
  bankAccount: string;
  bankName: string;
  /** Văn phòng làm việc (khác trụ sở đăng ký) — in ở phần liên hệ. */
  officeAddress: string;
  fanpage: string;
  tiktok: string;
}

export interface QuoteSettings {
  company: CompanyInfo;
  payment: PaymentTerms;
  contract: ContractTerms;
  notes: string;
}

export const DEFAULT_PAYMENT: PaymentTerms = {
  supportPercent: 100,
  upfrontAmount: 0,
  installmentMonths: 0,
  paymentMethod: 'DEDUCT_INCOME',
  travelSupportAmount: 0,
};

export const DEFAULT_CONTRACT: ContractTerms = {
  commitMonths: 12,
  inactivityMonths: 2,
  // 0 = để trống: hợp đồng in dòng chấm để hai bên tự thoả thuận (VD 20 ngày, 90 giờ).
  minLiveDaysPerMonth: 0,
  minLiveHoursPerMonth: 0,
  returnDays: 7,
  unreachableDays: 7,
  penaltyPercent: 8,
  lateInterestPercentYear: 10,
  ownershipTransfer: true,
  disputeAtCompanyCourt: true,
  allowDeductIncome: true,
  incomeSharePercent: 0,
  allowBuyout: true,
  agencyTermYears: 3,
  leaveAgencyIsBreach: true,
  compensateNewPercent: 100,
  compensateUsedPercent: 70,
};

export const DEFAULT_SETTINGS: QuoteSettings = {
  company: {
    // Theo masothue.com/0316927538 (tra 27/10/2026). Trên TikTok LIVE hoạt động dưới tên HP Media.
    name: 'CÔNG TY TNHH NHP ENTERTAINMENT',
    shortName: 'HP Media',
    address: '39/2 Đường XTT 59, Ấp 18, Xã Bà Điểm, Thành phố Hồ Chí Minh, Việt Nam',
    taxCode: '0316927538',
    representative: 'NGUYỄN HOÀNG PHỤNG',
    position: 'Giám đốc',
    phone: '0855555321',
    email: '',
    bankAccount: '',
    bankName: '',
    officeAddress: '94/1 Võ Oanh, Phường Thạnh Mỹ Tây, Thành phố Hồ Chí Minh',
    fanpage: 'https://www.facebook.com/hpvn.media/',
    tiktok: 'https://www.tiktok.com/@hpmedia.official',
  },
  payment: DEFAULT_PAYMENT,
  contract: DEFAULT_CONTRACT,
  notes:
    'Báo giá chưa gồm chi phí phát sinh (nếu có); KTV sẽ báo Creator trong quá trình setup.\n' +
    'Giá trên là giá tham khảo, có thể thay đổi theo nguồn cung hoặc tình trạng còn hàng. Một số món chưa gồm VAT.',
};

const int = (v: unknown, min: number, max: number, fallback: number) => {
  const n = Math.round(Number(v));
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
};
const text = (v: unknown, max: number) => (typeof v === 'string' ? v.trim().slice(0, max) : '');
const bool = (v: unknown, fallback: boolean) => (typeof v === 'boolean' ? v : fallback);

const MAX_MONEY = 100_000_000_000; // 100 tỷ
const PAYMENT_METHODS: PaymentMethod[] = ['CASH', 'DEDUCT_INCOME', 'MIXED'];

export function normalizePayment(raw: any, base: PaymentTerms = DEFAULT_PAYMENT): PaymentTerms {
  const r = raw && typeof raw === 'object' ? raw : {};
  return {
    supportPercent: int(r.supportPercent, 0, 100, base.supportPercent),
    upfrontAmount: int(r.upfrontAmount, 0, MAX_MONEY, base.upfrontAmount),
    installmentMonths: int(r.installmentMonths, 0, 60, base.installmentMonths),
    paymentMethod: PAYMENT_METHODS.includes(r.paymentMethod) ? r.paymentMethod : base.paymentMethod,
    travelSupportAmount: int(r.travelSupportAmount, 0, MAX_MONEY, base.travelSupportAmount),
  };
}

export function normalizeContract(raw: any, base: ContractTerms = DEFAULT_CONTRACT): ContractTerms {
  const r = raw && typeof raw === 'object' ? raw : {};
  return {
    // Chính sách 27/10: cho mượn tối đa 12 tháng.
    commitMonths: int(r.commitMonths, 1, 12, Math.min(12, base.commitMonths)),
    inactivityMonths: int(r.inactivityMonths, 1, 12, base.inactivityMonths),
    minLiveDaysPerMonth: int(r.minLiveDaysPerMonth, 0, 31, base.minLiveDaysPerMonth),
    minLiveHoursPerMonth: int(r.minLiveHoursPerMonth, 0, 744, base.minLiveHoursPerMonth),
    returnDays: int(r.returnDays, 1, 60, base.returnDays),
    unreachableDays: int(r.unreachableDays, 1, 60, base.unreachableDays),
    penaltyPercent: int(r.penaltyPercent, 0, 100, base.penaltyPercent),
    lateInterestPercentYear: int(r.lateInterestPercentYear, 0, 20, base.lateInterestPercentYear),
    ownershipTransfer: bool(r.ownershipTransfer, base.ownershipTransfer),
    disputeAtCompanyCourt: bool(r.disputeAtCompanyCourt, base.disputeAtCompanyCourt),
    allowDeductIncome: bool(r.allowDeductIncome, base.allowDeductIncome),
    incomeSharePercent: int(r.incomeSharePercent, 0, 100, base.incomeSharePercent ?? 0),
    allowBuyout: bool(r.allowBuyout, base.allowBuyout ?? true),
    agencyTermYears: int(r.agencyTermYears, 0, 10, base.agencyTermYears ?? 3),
    leaveAgencyIsBreach: bool(r.leaveAgencyIsBreach, base.leaveAgencyIsBreach ?? true),
    compensateNewPercent: int(r.compensateNewPercent, 0, 100, base.compensateNewPercent ?? 100),
    compensateUsedPercent: int(r.compensateUsedPercent, 0, 100, base.compensateUsedPercent ?? 70),
  };
}

function normalizePerson(raw: any): PersonInfo {
  const r = raw && typeof raw === 'object' ? raw : {};
  return {
    fullName: text(r.fullName, 120),
    dob: text(r.dob, 20),
    idNumber: text(r.idNumber, 20),
    idIssuedDate: text(r.idIssuedDate, 20),
    idIssuedPlace: text(r.idIssuedPlace, 160),
    address: text(r.address, 255),
    phone: text(r.phone, 32),
    email: text(r.email, 120),
    tiktokId: text(r.tiktokId, 80),
    tiktokUid: text(r.tiktokUid, 32),
    relation: text(r.relation, 60),
  };
}

/** Chỉ nhận key ảnh do POST /image/storages sinh ra (hp/<uuid>) — chặn URL lạ lọt vào Excel / ảnh. */
export function isImageKey(v: unknown): v is string {
  return typeof v === 'string' && /^hp\/[A-Za-z0-9-]{8,64}$/.test(v);
}

export function normalizeLine(raw: any, index: number): QuoteLine | null {
  if (!raw || typeof raw !== 'object') return null;
  const name = text(raw.name, 200);
  if (!name) return null;
  const itemId = Number(raw.itemId);
  return {
    key: text(raw.key, 40) || `l${index + 1}`,
    itemId: Number.isInteger(itemId) && itemId > 0 ? itemId : null,
    category: text(raw.category, 80) || 'Khác',
    name,
    brand: text(raw.brand, 60),
    unit: text(raw.unit, 20) || 'cái',
    price: int(raw.price, 0, MAX_MONEY, 0),
    qty: int(raw.qty, 1, 999, 1),
    supportPercent: int(raw.supportPercent, 0, 100, 0),
    serial: text(raw.serial, 120),
    note: text(raw.note, 255),
    imageKey: isImageKey(raw.imageKey) ? raw.imageKey : '',
    condition: raw.condition === 'USED' ? 'USED' : 'NEW',
    included: raw.included === true && raw.advance !== true,
    advance: raw.advance === true,
  };
}

export function normalizeCompany(
  raw: any,
  base: CompanyInfo = DEFAULT_SETTINGS.company
): CompanyInfo {
  const r = raw && typeof raw === 'object' ? raw : {};
  const pick = (k: keyof CompanyInfo, max: number) =>
    typeof r[k] === 'string' ? text(r[k], max) : base[k];
  return {
    name: pick('name', 200),
    shortName: pick('shortName', 60),
    address: pick('address', 255),
    taxCode: pick('taxCode', 20),
    representative: pick('representative', 120),
    position: pick('position', 80),
    phone: pick('phone', 32),
    email: pick('email', 120),
    bankAccount: pick('bankAccount', 40),
    bankName: pick('bankName', 160),
    officeAddress: pick('officeAddress', 255),
    fanpage: pick('fanpage', 255),
    tiktok: pick('tiktok', 255),
  };
}

export function normalizeSettings(raw: any): QuoteSettings {
  const r = raw && typeof raw === 'object' ? raw : {};
  return {
    company: normalizeCompany(r.company),
    payment: normalizePayment(r.payment),
    contract: normalizeContract(r.contract),
    notes: typeof r.notes === 'string' ? r.notes.slice(0, 2000) : DEFAULT_SETTINGS.notes,
  };
}

export function normalizeQuoteData(
  raw: any,
  settings: QuoteSettings = DEFAULT_SETTINGS
): QuoteData {
  const r = raw && typeof raw === 'object' ? raw : {};
  const lines = (Array.isArray(r.lines) ? r.lines : [])
    .slice(0, 200)
    .map((line: any, i: number) => normalizeLine(line, i))
    .filter((line: QuoteLine | null): line is QuoteLine => !!line);
  const guardian = r.guardian ? normalizePerson(r.guardian) : null;
  const hpCreator = r.hpCreator !== false;
  // Món tặng kèm của Creator HP Media luôn miễn phí — server ép lại khi lưu, không tin số % gửi lên.
  if (hpCreator) for (const line of lines) if (line.included) line.supportPercent = 100;
  // Món ứng trước: Công ty không hỗ trợ, Creator trả lại toàn bộ.
  for (const line of lines) if (line.advance) line.supportPercent = 0;
  return {
    lines,
    hpCreator,
    payment: normalizePayment(r.payment, settings.payment),
    contract: normalizeContract(r.contract, settings.contract),
    creator: normalizePerson(r.creator),
    guardian: guardian && guardian.fullName ? guardian : null,
    contractNo: text(r.contractNo, 60),
    signDate: text(r.signDate, 20),
    signPlace: text(r.signPlace, 120),
    deliveryAddress: text(r.deliveryAddress, 255),
    notes: typeof r.notes === 'string' ? r.notes.slice(0, 2000) : settings.notes,
    extraClauses: typeof r.extraClauses === 'string' ? r.extraClauses.slice(0, 4000) : '',
    handover: r.handover ? normalizeHandover(r.handover, lines) : null,
  };
}

export interface LineAmounts {
  amount: number;
  company: number;
  creator: number;
}

export function lineAmounts(line: QuoteLine): LineAmounts {
  const amount = line.price * line.qty;
  const company = Math.round((amount * line.supportPercent) / 100);
  return { amount, company, creator: amount - company };
}

export interface QuoteSummary {
  /** Tổng giá trị thiết bị + dịch vụ. */
  total: number;
  /** Phần Công ty hỗ trợ (theo %). */
  company: number;
  /** Phần Creator phải trả. */
  creator: number;
  /** Creator trả trước (không vượt phần Creator). */
  upfront: number;
  /** Creator còn phải trả sau khi trả trước. */
  remaining: number;
  /** Mỗi tháng (làm tròn lên tới 1.000 đ); 0 khi trả một lần. */
  monthly: number;
  travelSupport: number;
  /** Số Creator phải hoàn khi vi phạm: phần Công ty hỗ trợ + chi phí đi lại. */
  refundable: number;
  /** Tỷ lệ Công ty hỗ trợ thực tế trên tổng (%, 1 chữ số thập phân). */
  companyRate: number;
  categories: {
    category: string;
    total: number;
    company: number;
    creator: number;
    count: number;
  }[];
}

export function summarize(data: Pick<QuoteData, 'lines' | 'payment'>): QuoteSummary {
  let total = 0;
  let company = 0;
  const byCat = new Map<
    string,
    { category: string; total: number; company: number; creator: number; count: number }
  >();
  for (const line of data.lines) {
    const a = lineAmounts(line);
    total += a.amount;
    company += a.company;
    const c = byCat.get(line.category) ?? {
      category: line.category,
      total: 0,
      company: 0,
      creator: 0,
      count: 0,
    };
    c.total += a.amount;
    c.company += a.company;
    c.creator += a.creator;
    c.count += line.qty;
    byCat.set(line.category, c);
  }
  const creator = total - company;
  const upfront = Math.min(creator, Math.max(0, data.payment.upfrontAmount));
  const remaining = creator - upfront;
  const months = data.payment.installmentMonths;
  const monthly = months > 0 && remaining > 0 ? Math.ceil(remaining / months / 1000) * 1000 : 0;
  const travelSupport = data.payment.travelSupportAmount;
  return {
    total,
    company,
    creator,
    upfront,
    remaining,
    monthly,
    travelSupport,
    refundable: company + travelSupport,
    companyRate: total > 0 ? Math.round((company / total) * 1000) / 10 : 0,
    categories: Array.from(byCat.values()),
  };
}

/** Giá trị còn lại phần Công ty hỗ trợ sau `monthsUsed` tháng, khấu hao đều trong thời hạn cam kết. */
export function remainingSupportValue(
  company: number,
  commitMonths: number,
  monthsUsed: number
): number {
  if (commitMonths <= 0) return 0;
  const used = Math.min(commitMonths, Math.max(0, Math.floor(monthsUsed)));
  return Math.round((company * (commitMonths - used)) / commitMonths);
}

/** 65090000 → "65.090.000 đ" */
export function formatVnd(n: number): string {
  return `${Math.round(n)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, '.')} đ`;
}

const DIGITS = ['không', 'một', 'hai', 'ba', 'bốn', 'năm', 'sáu', 'bảy', 'tám', 'chín'];

function readTriple(n: number, full: boolean): string {
  const h = Math.floor(n / 100);
  const t = Math.floor((n % 100) / 10);
  const u = n % 10;
  const out: string[] = [];
  if (full || h > 0) out.push(`${DIGITS[h]} trăm`);
  if (t > 1) out.push(`${DIGITS[t]} mươi`);
  else if (t === 1) out.push('mười');
  else if ((full || h > 0) && u > 0) out.push('lẻ');
  if (u > 0) {
    if (u === 1 && t > 1) out.push('mốt');
    else if (u === 5 && t > 0) out.push('lăm');
    else if (u === 4 && t > 1) out.push('tư');
    else out.push(DIGITS[u]);
  }
  return out.join(' ');
}

/** Số tiền bằng chữ cho hợp đồng: 65090000 → "Sáu mươi lăm triệu không trăm chín mươi nghìn đồng". */
export function vndInWords(amount: number): string {
  let n = Math.round(Math.abs(amount));
  if (n === 0) return 'Không đồng';
  const units = ['', ' nghìn', ' triệu', ' tỷ'];
  const groups: number[] = [];
  while (n > 0) {
    groups.push(n % 1000);
    n = Math.floor(n / 1000);
  }
  const parts: string[] = [];
  for (let i = groups.length - 1; i >= 0; i--) {
    const g = groups[i];
    if (g === 0) continue;
    // Từ nhóm thứ 4 trở lên lặp lại "tỷ" (1.000 tỷ = "một nghìn tỷ").
    const unit = i >= 3 ? units[i % 3 === 0 ? 3 : i % 3] + (i > 3 ? ' tỷ' : '') : units[i];
    parts.push(readTriple(g, i < groups.length - 1) + unit);
  }
  const s = parts.join(' ').replace(/\s+/g, ' ').trim();
  return `${s.charAt(0).toUpperCase()}${s.slice(1)} đồng`;
}

const PER_UNIT = ['tháng', 'năm'];

/** Giá kèm đơn vị kỳ hạn: 2.000.000 đ/tháng; món thường chỉ hiện số tiền. */
export function priceText(line: Pick<QuoteLine, 'price' | 'unit'>): string {
  return PER_UNIT.includes(line.unit || '')
    ? `${formatVnd(line.price)}/${line.unit}`
    : formatVnd(line.price);
}

/** Số phải đền khi mất / hỏng không bảo hành một dòng: giá × SL × (100% nếu nhận mới, 70% nếu nhận cũ — theo điều khoản). */
export function compensationValue(
  line: Pick<QuoteLine, 'price' | 'qty' | 'condition'>,
  contract: Pick<ContractTerms, 'compensateNewPercent' | 'compensateUsedPercent'>
): number {
  const pct =
    line.condition === 'USED' ? contract.compensateUsedPercent : contract.compensateNewPercent;
  return Math.round((line.price * line.qty * pct) / 100);
}

/** Kết quả kiểm tra từng thiết bị lúc bàn giao (khoá theo QuoteLine.key). */
export interface HandoverCheck {
  /** Đã kiểm tra, hoạt động bình thường. */
  ok: boolean;
  serial?: string;
  note?: string;
}

/**
 * Bàn giao & nghiệm thu tại nơi Creator (KTV điền ngày setup). Lưu riêng qua PUT quotes/:id/handover nên
 * vẫn sửa được sau khi hợp đồng đã ký. Bản giấy Creator + KTV ký mang về Công ty kèm hợp đồng.
 */
export interface HandoverInfo {
  date?: string;
  technicianName?: string;
  technicianPhone?: string;
  checks: Record<string, HandoverCheck>;
  /** Đã hướng dẫn Creator sử dụng thiết bị, phần mềm. */
  trained: boolean;
  /** Đã LIVE thử thành công (hình, tiếng ổn định). */
  liveTested: boolean;
  /** Creator xác nhận đã nhận đủ, thiết bị hoạt động bình thường. */
  creatorConfirmed: boolean;
  /** KTV xác nhận đã kiểm tra đầy đủ. */
  technicianConfirmed: boolean;
  /** Bản giấy biên bản + hợp đồng có chữ ký đã nộp về Công ty. */
  paperReceived: boolean;
  note?: string;
  /** Server ghi khi cả Creator và KTV cùng xác nhận. */
  confirmedAt?: string | null;
  confirmedByName?: string | null;
  paperReceivedAt?: string | null;
  paperReceivedByName?: string | null;
}

export function normalizeHandover(raw: any, lines: Pick<QuoteLine, 'key'>[]): HandoverInfo {
  const r = raw && typeof raw === 'object' ? raw : {};
  const checks: Record<string, HandoverCheck> = {};
  const src = r.checks && typeof r.checks === 'object' ? r.checks : {};
  for (const l of lines) {
    const c = src[l.key];
    if (!c || typeof c !== 'object') continue;
    checks[l.key] = { ok: c.ok === true, serial: text(c.serial, 120), note: text(c.note, 255) };
  }
  const stamp = (v: unknown) => (typeof v === 'string' && v ? v.slice(0, 40) : null);
  return {
    date: text(r.date, 20),
    technicianName: text(r.technicianName, 120),
    technicianPhone: text(r.technicianPhone, 32),
    checks,
    trained: r.trained === true,
    liveTested: r.liveTested === true,
    creatorConfirmed: r.creatorConfirmed === true,
    technicianConfirmed: r.technicianConfirmed === true,
    paperReceived: r.paperReceived === true,
    note: typeof r.note === 'string' ? r.note.slice(0, 1000) : '',
    confirmedAt: stamp(r.confirmedAt),
    confirmedByName: stamp(r.confirmedByName),
    paperReceivedAt: stamp(r.paperReceivedAt),
    paperReceivedByName: stamp(r.paperReceivedByName),
  };
}

/** Bàn giao đủ: mọi thiết bị đã kiểm "ok" + hai bên đã xác nhận. */
export function handoverComplete(
  h: HandoverInfo | null | undefined,
  lines: Pick<QuoteLine, 'key'>[]
): boolean {
  if (!h) return false;
  return (
    lines.length > 0 &&
    lines.every((l) => h.checks[l.key]?.ok) &&
    h.creatorConfirmed &&
    h.technicianConfirmed
  );
}

/** Số hợp đồng đề xuất: <mã báo giá 4 số>/<năm>/HĐHT-HPM, VD 0002/2026/HĐHT-HPM. */
export function suggestContractNo(id: number, date: Date = new Date()): string {
  return `${String(id).padStart(4, '0')}/${date.getFullYear()}/HĐHT-HPM`;
}
